/**
 * Classic Risk-style territory roster.
 *
 * Geometry comes from https://github.com/raddrick/risk-map-svg, which stores
 * the board as React components. We import the component SVG path data through
 * riskMapSvgPaths.ts and keep game metadata here.
 */

import { riskCentroid, riskPath } from './riskMapSvgPaths.js';
import type { ModuleId, Territory, TerritoryId } from './types.js';

const t = (s: string): TerritoryId => s as TerritoryId;

interface RawTerritory {
  id: string;
  displayName: string;
  moduleId: ModuleId;
  adjacency: readonly string[];
  riskLevel: 'low' | 'medium' | 'high';
}

const RAW: readonly RawTerritory[] = [
  {
    id: "frontend-alaska",
    displayName: "Alaska",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-nwt","frontend-alberta","backend-kamchatka"],
    riskLevel: "low" as const,
  },
  {
    id: "frontend-nwt",
    displayName: "Northwest Territory",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-alaska","frontend-alberta","frontend-ontario","frontend-greenland"],
    riskLevel: "low" as const,
  },
  {
    id: "frontend-greenland",
    displayName: "Greenland",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-nwt","frontend-ontario","frontend-quebec","compliance-iceland"],
    riskLevel: "medium" as const,
  },
  {
    id: "frontend-alberta",
    displayName: "Alberta",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-alaska","frontend-nwt","frontend-ontario","frontend-western-us"],
    riskLevel: "low" as const,
  },
  {
    id: "frontend-ontario",
    displayName: "Ontario",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-nwt","frontend-greenland","frontend-quebec","frontend-western-us","frontend-eastern-us","frontend-alberta"],
    riskLevel: "medium" as const,
  },
  {
    id: "frontend-quebec",
    displayName: "Quebec",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-ontario","frontend-greenland","frontend-eastern-us"],
    riskLevel: "medium" as const,
  },
  {
    id: "frontend-western-us",
    displayName: "Western US",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-alberta","frontend-ontario","frontend-eastern-us","frontend-central-america"],
    riskLevel: "medium" as const,
  },
  {
    id: "frontend-eastern-us",
    displayName: "Eastern US",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-western-us","frontend-ontario","frontend-quebec","frontend-central-america"],
    riskLevel: "medium" as const,
  },
  {
    id: "frontend-central-america",
    displayName: "Central America",
    moduleId: "frontend" as ModuleId,
    adjacency: ["frontend-western-us","frontend-eastern-us","devops-venezuela"],
    riskLevel: "high" as const,
  },
  {
    id: "devops-venezuela",
    displayName: "Venezuela",
    moduleId: "devops" as ModuleId,
    adjacency: ["frontend-central-america","devops-peru","devops-brazil"],
    riskLevel: "medium" as const,
  },
  {
    id: "devops-peru",
    displayName: "Peru",
    moduleId: "devops" as ModuleId,
    adjacency: ["devops-venezuela","devops-brazil","devops-argentina"],
    riskLevel: "medium" as const,
  },
  {
    id: "devops-brazil",
    displayName: "Brazil",
    moduleId: "devops" as ModuleId,
    adjacency: ["devops-venezuela","devops-peru","devops-argentina","security-north-africa"],
    riskLevel: "high" as const,
  },
  {
    id: "devops-argentina",
    displayName: "Argentina",
    moduleId: "devops" as ModuleId,
    adjacency: ["devops-peru","devops-brazil"],
    riskLevel: "low" as const,
  },
  {
    id: "compliance-iceland",
    displayName: "Iceland",
    moduleId: "compliance" as ModuleId,
    adjacency: ["frontend-greenland","compliance-great-britain","compliance-scandinavia"],
    riskLevel: "low" as const,
  },
  {
    id: "compliance-great-britain",
    displayName: "Great Britain",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-iceland","compliance-scandinavia","compliance-northern-europe","compliance-western-europe"],
    riskLevel: "low" as const,
  },
  {
    id: "compliance-scandinavia",
    displayName: "Scandinavia",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-iceland","compliance-great-britain","compliance-northern-europe","compliance-ukraine"],
    riskLevel: "medium" as const,
  },
  {
    id: "compliance-northern-europe",
    displayName: "Northern Europe",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-great-britain","compliance-scandinavia","compliance-ukraine","compliance-southern-europe","compliance-western-europe"],
    riskLevel: "medium" as const,
  },
  {
    id: "compliance-western-europe",
    displayName: "Western Europe",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-great-britain","compliance-northern-europe","compliance-southern-europe","security-north-africa"],
    riskLevel: "medium" as const,
  },
  {
    id: "compliance-southern-europe",
    displayName: "Southern Europe",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-western-europe","compliance-northern-europe","compliance-ukraine","backend-middle-east","security-egypt","security-north-africa"],
    riskLevel: "medium" as const,
  },
  {
    id: "compliance-ukraine",
    displayName: "Ukraine",
    moduleId: "compliance" as ModuleId,
    adjacency: ["compliance-scandinavia","compliance-northern-europe","compliance-southern-europe","backend-ural","backend-afghanistan","backend-middle-east"],
    riskLevel: "high" as const,
  },
  {
    id: "security-north-africa",
    displayName: "North Africa",
    moduleId: "security" as ModuleId,
    adjacency: ["devops-brazil","compliance-western-europe","compliance-southern-europe","security-egypt","security-east-africa","security-congo"],
    riskLevel: "medium" as const,
  },
  {
    id: "security-egypt",
    displayName: "Egypt",
    moduleId: "security" as ModuleId,
    adjacency: ["security-north-africa","compliance-southern-europe","backend-middle-east","security-east-africa"],
    riskLevel: "high" as const,
  },
  {
    id: "security-east-africa",
    displayName: "East Africa",
    moduleId: "security" as ModuleId,
    adjacency: ["security-egypt","backend-middle-east","security-congo","security-south-africa","security-madagascar"],
    riskLevel: "high" as const,
  },
  {
    id: "security-congo",
    displayName: "Congo",
    moduleId: "security" as ModuleId,
    adjacency: ["security-north-africa","security-east-africa","security-south-africa"],
    riskLevel: "medium" as const,
  },
  {
    id: "security-south-africa",
    displayName: "South Africa",
    moduleId: "security" as ModuleId,
    adjacency: ["security-congo","security-east-africa","security-madagascar"],
    riskLevel: "low" as const,
  },
  {
    id: "security-madagascar",
    displayName: "Madagascar",
    moduleId: "security" as ModuleId,
    adjacency: ["security-east-africa","security-south-africa"],
    riskLevel: "low" as const,
  },
  {
    id: "backend-ural",
    displayName: "Ural",
    moduleId: "backend" as ModuleId,
    adjacency: ["compliance-ukraine","backend-siberia","backend-china","backend-afghanistan"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-siberia",
    displayName: "Siberia",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-ural","backend-yakutsk","backend-irkutsk","backend-mongolia","backend-china"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-yakutsk",
    displayName: "Yakutsk",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-siberia","backend-irkutsk","backend-kamchatka"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-kamchatka",
    displayName: "Kamchatka",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-yakutsk","backend-irkutsk","backend-mongolia","backend-japan","frontend-alaska"],
    riskLevel: "high" as const,
  },
  {
    id: "backend-irkutsk",
    displayName: "Irkutsk",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-siberia","backend-yakutsk","backend-kamchatka","backend-mongolia"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-mongolia",
    displayName: "Mongolia",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-siberia","backend-irkutsk","backend-kamchatka","backend-japan","backend-china"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-japan",
    displayName: "Japan",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-kamchatka","backend-mongolia"],
    riskLevel: "low" as const,
  },
  {
    id: "backend-afghanistan",
    displayName: "Afghanistan",
    moduleId: "backend" as ModuleId,
    adjacency: ["compliance-ukraine","backend-ural","backend-china","backend-india","backend-middle-east"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-china",
    displayName: "China",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-ural","backend-siberia","backend-mongolia","backend-siam","backend-india","backend-afghanistan"],
    riskLevel: "high" as const,
  },
  {
    id: "backend-middle-east",
    displayName: "Middle East",
    moduleId: "backend" as ModuleId,
    adjacency: ["compliance-ukraine","backend-afghanistan","backend-india","compliance-southern-europe","security-egypt","security-east-africa"],
    riskLevel: "high" as const,
  },
  {
    id: "backend-india",
    displayName: "India",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-middle-east","backend-afghanistan","backend-china","backend-siam"],
    riskLevel: "medium" as const,
  },
  {
    id: "backend-siam",
    displayName: "Siam",
    moduleId: "backend" as ModuleId,
    adjacency: ["backend-india","backend-china","database-indonesia"],
    riskLevel: "medium" as const,
  },
  {
    id: "database-indonesia",
    displayName: "Indonesia",
    moduleId: "database" as ModuleId,
    adjacency: ["backend-siam","database-new-guinea","database-western-australia"],
    riskLevel: "high" as const,
  },
  {
    id: "database-new-guinea",
    displayName: "New Guinea",
    moduleId: "database" as ModuleId,
    adjacency: ["database-indonesia","database-western-australia","database-eastern-australia"],
    riskLevel: "medium" as const,
  },
  {
    id: "database-western-australia",
    displayName: "Western Australia",
    moduleId: "database" as ModuleId,
    adjacency: ["database-indonesia","database-new-guinea","database-eastern-australia"],
    riskLevel: "medium" as const,
  },
  {
    id: "database-eastern-australia",
    displayName: "Eastern Australia",
    moduleId: "database" as ModuleId,
    adjacency: ["database-new-guinea","database-western-australia"],
    riskLevel: "medium" as const,
  },
];

function buildAdjacencyMap(): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const r of RAW) map.set(r.id, new Set());
  for (const r of RAW) {
    for (const partner of r.adjacency) {
      const a = map.get(r.id);
      const b = map.get(partner);
      if (!a || !b) {
        throw new Error(`Adjacency declared for unknown territory: ${r.id} -> ${partner}`);
      }
      a.add(partner);
      b.add(r.id);
    }
  }
  return map;
}

const ADJACENCY = buildAdjacencyMap();

export const TERRITORIES: readonly Territory[] = RAW.map((r) => ({
  id: t(r.id),
  displayName: r.displayName,
  moduleId: r.moduleId,
  adjacencyIds: Object.freeze([...(ADJACENCY.get(r.id) ?? [])].map(t)),
  centroid: riskCentroid(r.id),
  pathD: riskPath(r.id),
  riskLevel: r.riskLevel,
}));

export const TERRITORIES_BY_ID: ReadonlyMap<TerritoryId, Territory> = new Map(
  TERRITORIES.map((tr) => [tr.id, tr]),
);

export const TERRITORY_IDS_BY_MODULE: Readonly<Record<ModuleId, readonly TerritoryId[]>> = (() => {
  const out: Record<ModuleId, TerritoryId[]> = {
    frontend: [],
    backend: [],
    database: [],
    security: [],
    devops: [],
    compliance: [],
  };
  for (const tr of TERRITORIES) out[tr.moduleId].push(tr.id);
  return Object.freeze({
    frontend: Object.freeze(out.frontend),
    backend: Object.freeze(out.backend),
    database: Object.freeze(out.database),
    security: Object.freeze(out.security),
    devops: Object.freeze(out.devops),
    compliance: Object.freeze(out.compliance),
  });
})();
